import { useState, useEffect } from 'react';
import { translations, type Language } from '../config/translations';

// Single source of truth for translations lives in ../config/translations.
export type { Language };
export { translations };

// Language detection (localStorage -> browser -> 'en')
export const detectLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';

  const savedLanguage = localStorage.getItem('userLanguage');
  if (savedLanguage && ['en', 'sv', 'es', 'de'].includes(savedLanguage)) {
    return savedLanguage as Language;
  }

  const browserLanguage = navigator.language.toLowerCase();
  if (browserLanguage.startsWith('sv')) return 'sv';
  if (browserLanguage.startsWith('es')) return 'es';
  if (browserLanguage.startsWith('de')) return 'de';

  return 'en';
};

// --- Shared language store -------------------------------------------------
// Every useTranslations() instance subscribes here, so a language change made
// anywhere (the language switcher, server preference load) updates ALL
// components live — not just the ones that receive `t` as a prop.
let currentLanguage: Language = detectLanguage();
const listeners = new Set<(lang: Language) => void>();

export const getGlobalLanguage = (): Language => currentLanguage;

export const setGlobalLanguage = (lang: Language): void => {
  if (!['en', 'sv', 'es', 'de'].includes(lang)) return;
  currentLanguage = lang;
  try {
    localStorage.setItem('userLanguage', lang);
  } catch (e) {
    /* ignore storage errors */
  }
  listeners.forEach((fn) => fn(lang));
};

// Hook for managing translations
export const useTranslations = () => {
  const [language, setLanguageState] = useState<Language>(currentLanguage);

  useEffect(() => {
    const listener = (lang: Language) => setLanguageState(lang);
    listeners.add(listener);
    // Catch any change that happened between module init and mount.
    if (language !== currentLanguage) setLanguageState(currentLanguage);
    return () => {
      listeners.delete(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = (key: string): string => {
    const dict = translations[language] as Record<string, string>;
    const en = translations.en as Record<string, string>;
    return dict[key] ?? en[key] ?? key;
  };

  const saveUserLanguage = (lang: Language) => {
    setGlobalLanguage(lang);
  };

  const loadUserLanguage = async () => {
    try {
      const response = await fetch('/api/settings/language', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.language && ['en', 'sv', 'es', 'de'].includes(data.language)) {
          setGlobalLanguage(data.language as Language);
        }
      }
    } catch (error) {
      console.error('Error loading user language:', error);
    }
  };

  return {
    language,
    setLanguage: setGlobalLanguage,
    t,
    saveUserLanguage,
    loadUserLanguage
  };
};
